import React from 'react';
import { AdditionalBookingSectionEntity } from 'Models/Entities';
import { tripDirectionTypes } from 'Models/Enums';
import { compareFnByNumberAsc } from 'Util/_HumanWritten/SortUtils';
import { TabSelected } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardTypes';
import { AddOnSectionCard } from 'Views/Components/AddOns/AddOnSectionCard';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { AddOnsTabData } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { observer } from 'mobx-react';
import { AddOnOption } from './AddOnOption';
import { canAdjustRestrictedAddOns } from 'Util/_HumanWritten/addOnPermissions';

export interface NewAddOnSectionProps {
	addOnSection: AdditionalBookingSectionEntity;
	direction: tripDirectionTypes;
	canBeAppliedToReturnTrip: boolean;
	capacityRestrictions?: AddOnsTabData;
	bookingType: TabSelected;
}

export function AddOnSection({ addOnSection, capacityRestrictions, bookingType }: NewAddOnSectionProps) {
	const { oldWizardData } = useBookingWizardDataStore();

	const isAlteration = oldWizardData?.wizardMode === 'ALTERATION';

	// Collect existing add-on IDs from the original booking for alteration mode
	const existingAddOnIds = new Set<string>();
	if (isAlteration) {
		for (const opt of oldWizardData?.innerWizardData?.departingTripOptions ?? []) {
			if (opt.amount > 0) {
				existingAddOnIds.add(opt.optionId);
			}
		}
		for (const opt of oldWizardData?.innerWizardData?.returningTripOptions ?? []) {
			if (opt.amount > 0) {
				existingAddOnIds.add(opt.optionId);
			}
		}
	}

	const addOns = addOnSection.additionalBookingOptions
		.filter(option => {
			// Restricted add-ons are hidden unless the caller may adjust them.
			if (option.isRestricted && !canAdjustRestrictedAddOns()) {
				if (!isAlteration) {
					return false;
				}
				return existingAddOnIds.has(option.id);
			}

			if (isAlteration) {
				// Exclude add-ons that can only be added during booking creation
				if (option.isInitialBookingOnly) {
					return false;
				}
				// For disabled options/sections, only show if user already has them
				if (option.disabled || addOnSection.disabled) {
					return existingAddOnIds.has(option.id);
				}
			} else if (option.disabled || addOnSection.disabled) {
				// For new bookings, hide disabled options
				return false;
			}
			if (option.passengerAddOn && bookingType === 'passenger') {
				return true;
			}
			if (option.vehicleAddOn && bookingType === 'vehicle') {
				return true;
			}
			if (option.freightAddOn && bookingType === 'freight') {
				return true;
			}
			return false;
		})
		.sort(compareFnByNumberAsc(x => x.order));

	return (
		<AddOnSectionCard
			name={addOnSection.name}
			iconName={addOnSection.iconName ?? undefined}
			colour={addOnSection.colour}
			information={addOnSection.information}
		>
			{addOns.map(x => (
				<AddOnOption
					key={x.id}
					addOn={x}
					sectionAddOns={addOns}
					maxSectionAddOnPerBooking={addOnSection.maxAdditionalOptionsPerBooking}
					departureCapacityRestriction={capacityRestrictions?.departureTickets?.find(y => y.addOnId === x.id)}
					returnCapacityRestriction={capacityRestrictions?.returningTickets?.find(y => y.addOnId === x.id)}
					sectionDisabled={addOnSection.disabled}
				/>
			))}
		</AddOnSectionCard>
	);
}

export default observer(AddOnSection);
